import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import SEO, { breadcrumbs } from '../components/common/SEO'
import ResponsiveImage from '../components/common/ResponsiveImage'
import { teamMembers } from '../data/team'
import { BOOKING_URL, SITE_URL } from '../data/site'

const coreValues = [
  {
    title: 'Autonomy & Confidence in Your Body',
    description:
      'Care is a partnership. Whether you come in occasionally or regularly, our goal is to support your understanding of your body and build confidence in how it moves and responds over time.',
  },
  {
    title: 'Evidence-Based, Human-Centered Care',
    description:
      'We ground our work in research and training, while adapting it to the individual in front of us. Your experience, comfort, and feedback guide how evidence is applied.',
  },
  {
    title: 'Nervous System Informed Treatment',
    description:
      'Recovery is influenced by more than tissues alone. We integrate an understanding of the nervous system into massage, acupuncture, movement guidance, and recovery strategies to support sleep, stress regulation, and adaptation.',
  },
  {
    title: 'Wellness That Fits Your Life',
    description:
      'Your treatment should prepare you for the real demands of your day. We tailor care to your lifestyle and comfort level so it remains relevant, effective, and capable of creating lasting change.',
  },
]

const HERO_BASE_STYLE = {
  backgroundImage: "url('/optimized/orange-phase-wide-1376.webp')",
}
const HERO_OVERLAY_STYLE = {
  backgroundImage:
    'linear-gradient(to bottom, rgba(245,240,232,0) 0%, rgba(245,240,232,0.4) 100%)',
  opacity: 1,
}

const MemberDetail = ({ member, onClose }) => (
  <div className="relative pt-16">
    <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-10">
      <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-[3px] border-cream-200 shadow-lg">
        <ResponsiveImage
          src={member.image}
          alt={`${member.name}, ${member.fullTitle}`}
          sizes="128px"
          className="w-full h-full object-cover object-[center_18%]"
        />
      </div>
    </div>

    <div className="bg-white rounded-2xl border border-dark-200/25 px-8 md:px-12 pt-20 pb-10 shadow-sm relative">
      <button
        type="button"
        onClick={onClose}
        className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center text-dark-400 hover:text-dark-700 transition-colors"
        aria-label="Close detail view"
        tabIndex={onClose ? 0 : -1}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="flex items-baseline justify-between gap-4 mb-8">
        <h3 className="font-display text-dark-700 text-2xl md:text-3xl font-medium">
          {member.name}
        </h3>
        <span className="font-sans text-dark-400 text-xs tracking-[0.1em] uppercase flex-shrink-0">
          {member.title}
        </span>
      </div>

      <div className="space-y-4 mb-10">
        {member.bio.split('\n\n').map((paragraph, i) => (
          <p key={i} className="font-sans text-dark-500 text-base leading-relaxed">
            {paragraph}
          </p>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-10">
        {member.specialties?.length > 0 && (
          <div>
            <h4 className="font-display text-dark-700 text-base font-semibold mb-3">Specialties</h4>
            <ul className="space-y-2">
              {member.specialties.map((s) => (
                <li
                  key={s}
                  className="flex items-start gap-2 font-sans text-dark-500 text-base leading-relaxed"
                >
                  <span className="w-1 h-1 rounded-full bg-brand-orange-500 mt-2 flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
          </div>
        )}

        {member.treatmentStyle && (
          <div>
            <h4 className="font-display text-dark-700 text-base font-semibold mb-3">Treatment Style</h4>
            <div className="space-y-2">
              {member.treatmentStyle.pressure && (
                <p className="font-sans text-dark-500 text-base leading-relaxed">
                  <span className="text-dark-700 font-medium">Pressure:</span>{' '}
                  {member.treatmentStyle.pressure}
                </p>
              )}
              {member.treatmentStyle.approach && (
                <p className="font-sans text-dark-500 text-base leading-relaxed">
                  <span className="text-dark-700 font-medium">Approach:</span>{' '}
                  {member.treatmentStyle.approach}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-10">
        {member.certifications?.length > 0 && (
          <div>
            <h4 className="font-display text-dark-700 text-base font-semibold mb-3">
              Certifications &amp; Training
            </h4>
            <ul className="space-y-2">
              {member.certifications.map((q) => (
                <li
                  key={q}
                  className="flex items-start gap-2 font-sans text-dark-500 text-base leading-relaxed"
                >
                  <span className="w-1 h-1 rounded-full bg-brand-green-600 mt-2 flex-shrink-0" />
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {member.outsideClinic && (
          <div>
            <h4 className="font-display text-dark-700 text-base font-semibold mb-3">Outside the Clinic</h4>
            <p className="font-sans text-dark-500 text-base leading-relaxed">{member.outsideClinic}</p>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          tabIndex={onClose ? 0 : -1}
          className="border border-dark-700 text-dark-700 px-6 py-2.5 font-sans text-xs tracking-[0.15em] uppercase
                     hover:bg-dark-700 hover:text-white transition-all duration-200"
        >
          Go Back
        </button>
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={onClose ? 0 : -1}
          className="bg-brand-orange-500 text-white px-6 py-2.5 font-sans text-xs tracking-[0.15em] uppercase
                     hover:bg-brand-orange-400 transition-colors duration-200"
        >
          Book an Appointment
        </a>
        <Link
          to={`/team/${member.slug}`}
          tabIndex={onClose ? 0 : -1}
          className="font-sans text-xs tracking-[0.15em] uppercase text-brand-orange-600 hover:text-brand-orange-500 underline underline-offset-4 px-2"
        >
          View full profile
        </Link>
      </div>
    </div>
  </div>
)

const AboutPage = () => {
  const [expandedMember, setExpandedMember] = useState(null)
  const openMember = teamMembers.find((m) => m.id === expandedMember) || null

  return (
    <>
      <SEO
        title="About Our Midtown Toronto Clinic"
        description="Meet the Recreo Rehab team: registered massage therapists, a certified athletic therapist, and a naturopathic doctor at Yonge & Eglinton."
        path="/about"
        keywords="about Recreo Rehab, Toronto massage therapy clinic, athletic therapist Toronto, RMT team Yonge and Eglinton, Midtown Toronto therapists"
        structuredData={[
          breadcrumbs([
            { name: 'Home', path: '/' },
            { name: 'About', path: '/about' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About Recreo Rehab',
            url: `${SITE_URL}/about`,
            description:
              'Recreo Rehab is a Midtown Toronto clinic offering massage therapy, athletic therapy, and naturopathic medicine.',
            mainEntity: { '@id': `${SITE_URL}/#business` },
            hasPart: teamMembers.map((member) => ({
              '@type': 'ProfilePage',
              url: `${SITE_URL}/team/${member.slug}`,
              name: `${member.name}, ${member.title}`,
            })),
          },
        ]}
      />

      {/* ── Hero ── */}
      <div className="bg-cream-200">
        <section className="relative py-32 pt-40 overflow-hidden rounded-b-[24px] md:rounded-b-[100px] z-10">
        <div
          className="absolute inset-0 bg-cream-200 bg-cover bg-center"
          style={HERO_BASE_STYLE}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={HERO_OVERLAY_STYLE}
          aria-hidden="true"
        />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <motion.p
            className="font-script text-dark-600 italic text-base md:text-lg mb-6"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: 'easeOut' }}
          >
            Midtown Toronto&apos;s path to pain-free movement
          </motion.p>
          <motion.h1
            className="font-display text-dark-800 text-3xl md:text-5xl lg:text-6xl leading-[1.2] max-w-4xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: 'easeOut' }}
          >
            Hands-on care to help you understand your pain, move better, and feel more confident in your body.
          </motion.h1>
        </div>
      </section>
      </div>

      {/* ── Core Values ── */}
      <section className="bg-cream-200 py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <span className="font-script text-brand-orange-500 italic text-lg block mb-3">
                Our Values
              </span>
              <h2 className="font-display text-dark-700 text-3xl md:text-4xl lg:text-[2.75rem] font-semibold leading-[1.15] mb-6">
                What guides our hands
              </h2>
              <p className="font-body text-dark-500 text-base md:text-lg leading-relaxed">
                Every session is shaped by a set of principles we return to again and again — grounded in science, centered on you, and designed to create change that lasts beyond the treatment table.
              </p>
            </motion.div>

            <div className="space-y-5">
              {coreValues.map((value, index) => (
                <motion.div
                  key={value.title}
                  className="bg-white rounded-xl border border-dark-200/30 p-6 md:p-8 shadow-sm"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: index * 0.12, ease: 'easeOut' }}
                >
                  <h3 className="font-display text-dark-700 text-lg font-medium">
                    {value.title}
                  </h3>
                  <div className="border-t border-dark-200/40 my-3" />
                  <p className="font-sans text-dark-500 text-base leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Meet the Team (MMT-style expandable cards) ── */}
      <section className="bg-cream-100 py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16 md:mb-20"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <span className="font-sans text-dark-500 text-xs tracking-[0.2em] uppercase block mb-3">
              Meet the Team
            </span>
            <h2 className="font-display text-dark-700 text-3xl md:text-4xl lg:text-5xl font-semibold">
              Get to know our therapists
            </h2>
          </motion.div>

          {/* Card grid / expanded detail — stays on this page */}
          <AnimatePresence mode="wait">
            {openMember === null ? (
              <motion.div
                key="grid"
                className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {teamMembers.map((member) => (
                  <div key={member.id} className="relative pt-14">
                    <div className="absolute -top-2 left-8 z-10">
                      <div className="w-24 h-24 rounded-full overflow-hidden border-[3px] border-cream-200 shadow-md">
                        <ResponsiveImage
                          src={member.image}
                          alt={`${member.name}, ${member.fullTitle}`}
                          sizes="96px"
                          className="w-full h-full object-cover object-[center_18%]"
                        />
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-dark-200/25 px-8 pt-16 pb-8 shadow-sm">
                      <div className="flex items-baseline justify-between gap-4 mb-4">
                        <h3 className="font-display text-dark-700 text-xl md:text-2xl font-medium">
                          {member.name}
                        </h3>
                        <span className="font-sans text-dark-400 text-xs tracking-[0.1em] uppercase flex-shrink-0">
                          {member.title}
                        </span>
                      </div>

                      <p className="font-sans text-dark-500 text-base leading-relaxed mb-6">
                        {member.shortBio}
                      </p>

                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={() => setExpandedMember(member.id)}
                          aria-expanded={false}
                          className="inline-block border border-dark-700 text-dark-700 px-5 py-2.5 font-sans text-xs tracking-[0.15em] uppercase
                                     hover:bg-dark-700 hover:text-white transition-all duration-200"
                        >
                          Read More
                        </button>
                        <a
                          href={BOOKING_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block bg-brand-orange-500 text-white px-5 py-2.5 font-sans text-xs tracking-[0.15em] uppercase
                                     hover:bg-brand-orange-400 transition-colors duration-200"
                        >
                          Book
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key={`detail-${openMember.id}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
              >
                <MemberDetail member={openMember} onClose={() => setExpandedMember(null)} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Full bios stay in the prerendered HTML so search engines can read
              them without clicking. Hidden from visitors; the card above is
              what they see. */}
          <div hidden>
            {teamMembers.map((member) => (
              <MemberDetail key={member.slug} member={member} onClose={null} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Recreo ── */}
      <section className="bg-cream-200 py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-0 rounded-[20px] overflow-hidden border border-dark-200/30">
            <div className="relative min-h-[360px] md:min-h-[480px]">
              <ResponsiveImage
                src="/about-space.jpg"
                alt="Recreo Rehab treatment room with massage table, natural light, and city views"
                sizes="(min-width: 768px) 560px, 100vw"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            </div>

            <div className="bg-cream-100 flex flex-col justify-center px-8 md:px-12 lg:px-16 py-14 md:py-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <span className="font-script text-brand-orange-500 italic text-lg block mb-3">
                  Why Recreo
                </span>
                <h2 className="font-display text-dark-700 text-2xl md:text-3xl lg:text-4xl font-semibold leading-[1.15] mb-6">
                  A space designed for your recovery
                </h2>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.8, delay: 0.15, ease: 'easeOut' }}
                className="space-y-4"
              >
                <p className="font-sans text-dark-500 text-base leading-relaxed">
                  We believe great care goes beyond treatment alone. It means
                  feeling heard, having your concerns taken seriously, and
                  leaving with a clearer understanding of your body and what it
                  needs.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-orange-500 mt-2 flex-shrink-0" />
                    <span className="font-sans text-dark-500 text-base leading-relaxed">
                      Personalized care built around your goals and lived experience
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-green-600 mt-2 flex-shrink-0" />
                    <span className="font-sans text-dark-500 text-base leading-relaxed">
                      Time to listen, collaborate, and adapt treatment as your needs change
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-orange-500 mt-2 flex-shrink-0" />
                    <span className="font-sans text-dark-500 text-base leading-relaxed">
                      A calm environment designed to support recovery and wellbeing
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-green-600 mt-2 flex-shrink-0" />
                    <span className="font-sans text-dark-500 text-base leading-relaxed">
                      Education and practical strategies to help you navigate life between appointments
                    </span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default AboutPage
